// Pure helpers of the calendar's day popover ("programmer pour …"): the departments the user may
// schedule in, the default form, the REPEAT presets anchored on the clicked day, validation, the
// request bodies (POST /api/tasks with dueAt, POST /api/routines with schedule.start = that day)
// and the live preview line. DOM-free.
import { t } from '../../i18n/fr.js';
import { can, sortedFloors, floorDepartments, deptAgents } from '../../store.js';
import { atTime, defaultTimeFor, dayKey, startOfDay, longDayLabel, shortDayLabel } from './grid.js';
import {
  presetForDay, builderToSchedule, validateSchedule, describeRhythm, describeBounds, nextRun, sortDays, isTime,
  MAX_HOURLY_EVERY,
} from '../automations/schedule.js';
import { firstLineOf, ROUTINE_PROMPT_MAX } from '../automations/model.js';
import './strings.js';

/** The repeat choices of the popover (SPEC §11: quotidien, jours ouvrés, hebdo, mensuel, toutes les N heures). */
export const REPEAT_KINDS = ['daily', 'weekdays', 'weekly', 'monthly', 'hourly'];
/** Largest task text the popover sends (the server's default limits.maxTaskInput). */
export const TASK_TEXT_MAX = 20000;
/** A scheduled task must be at least this far in the future when it is added. */
export const MIN_LEAD_MS = 30 * 1000;
const MODEL_RE = /^[A-Za-z0-9._:\-/[\]]{1,80}$/;

/**
 * Departments where the user may schedule a task (level ≥ operate), in building order, each with
 * `floorName`. `floorId` '' means every floor.
 * @param {object} state app state
 * @param {string} [floorId]
 * @returns {object[]}
 */
export function schedulableDepartments(state, floorId = '') {
  const { floors = [], departments = [] } = state?.office || {};
  const out = [];
  for (const f of sortedFloors(floors)) {
    if (floorId && f.id !== floorId) continue;
    for (const d of floorDepartments(departments, f.id)) {
      if (can(state, 'operate', { deptId: d.id })) out.push({ ...d, floorName: f.name });
    }
  }
  return out;
}

/** Agents of a department, lead first. */
export function agentsOfDept(state, deptId) {
  return deptAgents(state?.office?.agents || [], deptId);
}

/**
 * The agent a routine created from the popover is given when "Auto" is chosen: the department's
 * lead (leadAgentId, else an agent flagged lead, else the first agent).
 * @param {object} state
 * @param {object} dept
 * @returns {string} '' when the department has no agent
 */
export function routineAgentFor(state, dept) {
  const agents = agentsOfDept(state, dept?.id);
  if (dept?.leadAgentId && agents.some((a) => a.id === dept.leadAgentId)) return dept.leadAgentId;
  return agents[0]?.id || '';
}

/**
 * "Je valide avant" is on unless the agent (when one is chosen) is autonomous.
 * @param {object|null} agent
 */
export function approveDefault(agent) {
  return agent?.autonomy !== 'auto';
}

/**
 * The initial state of the popover form for a day.
 * @param {{state: object, dayMs: number, now?: number, depts: object[], preferredDeptIds?: Iterable<string>}} opts
 */
export function defaultDayForm({ state, dayMs, now = Date.now(), depts = [], preferredDeptIds = [] }) {
  const preferred = [...preferredDeptIds].find((id) => depts.some((d) => d.id === id));
  const current = state?.ui?.deptFilter && depts.some((d) => d.id === state.ui.deptFilter) ? state.ui.deptFilter : '';
  const deptId = preferred || current || depts[0]?.id || '';
  const time = defaultTimeFor(dayMs, now);
  return {
    deptId, agentId: '', time, model: '', text: '', repeat: false, repeatKind: 'weekly',
    days: [new Date(dayMs).getDay()], everyHours: 2, until: '', weekdaysOnly: false, approve: true,
  };
}

/**
 * The routine schedule of the form, starting on the clicked day.
 * @param {object} form
 * @param {number} dayMs
 * @returns {object} Schedule
 */
export function dayFormSchedule(form, dayMs) {
  const kind = REPEAT_KINDS.includes(form.repeatKind) ? form.repeatKind : 'weekly';
  const b = presetForDay(kind, dayMs, { at: form.time, days: form.days, everyHours: form.everyHours, to: form.until });
  if (kind === 'weekly') b.days = sortDays(form.days || []);
  if (kind === 'hourly') {
    b.everyHours = Number.parseInt(String(form.everyHours ?? ''), 10);
    b.weekdaysOnly = !!form.weekdaysOnly;
    b.to = isTime(form.until) ? form.until : '';
  }
  return builderToSchedule(b);
}

/**
 * The instant of the one-off task (null when the time is invalid).
 * @param {object} form
 * @param {number} dayMs
 */
export function dayFormDueAt(form, dayMs) {
  return atTime(dayMs, form.time);
}

/**
 * Checks the form; returns field → French message (empty object when valid).
 * @param {object} form
 * @param {{dayMs: number, now?: number, state?: object, depts?: object[]}} opts
 */
export function validateDayForm(form, { dayMs, now = Date.now(), state = null, depts = [] }) {
  const errors = {};
  const dept = depts.find((d) => d.id === form.deptId);
  if (!dept) errors.deptId = t('cal.form.noDept');
  const text = String(form.text || '').trim();
  if (!text) errors.text = t('cal.form.empty');
  else if (form.repeat && text.length > ROUTINE_PROMPT_MAX) errors.text = t('cal.form.tooLong', { max: ROUTINE_PROMPT_MAX });
  else if (text.length > TASK_TEXT_MAX) errors.text = t('cal.form.tooLong', { max: TASK_TEXT_MAX });
  if (!isTime(form.time)) errors.time = t('cal.form.timeRequired');
  if (form.model && !MODEL_RE.test(String(form.model).trim())) errors.model = t('cal.form.badModel');
  if (startOfDay(dayMs) < startOfDay(now)) {
    errors.form = t('cal.form.pastDay');
    return errors;
  }
  if (form.repeat) {
    if (dept && state && !can(state, 'manage', { deptId: dept.id })) errors.form = t('cal.form.repeatNeedsManage');
    else if (dept && state && !routineAgentFor(state, dept) && !form.agentId) errors.form = t('cal.form.noRoutineAgent');
    if (!errors.time) {
      const schedule = dayFormSchedule(form, dayMs);
      const problems = validateSchedule(schedule);
      if (problems.length) errors.schedule = problems[0];
      else if (nextRun(schedule, now) === null) errors.schedule = t('cal.form.noRun');
    }
  } else if (!errors.time) {
    const due = dayFormDueAt(form, dayMs);
    if (due === null || due < now + MIN_LEAD_MS) errors.time = t('cal.form.past');
  }
  return errors;
}

/**
 * POST /api/tasks body of a one-off task on that day.
 * @param {object} form
 * @param {object} dept
 * @param {number} dayMs
 */
export function dayTaskBody(form, dept, dayMs) {
  const body = {
    floorId: dept.floorId, deptId: dept.id, input: String(form.text || '').trim(),
    autonomy: form.approve ? 'approve' : 'auto', dueAt: dayFormDueAt(form, dayMs), source: { kind: 'calendar' },
  };
  if (form.agentId) body.agentId = form.agentId;
  const model = String(form.model || '').trim();
  if (model) body.model = model;
  return body;
}

/**
 * POST /api/routines body of a routine starting that day (created active: the user asked for it).
 * @param {object} form
 * @param {object} dept
 * @param {number} dayMs
 * @param {object} state app state (for the default agent)
 */
export function dayRoutineBody(form, dept, dayMs, state) {
  const prompt = String(form.text || '').trim();
  const body = {
    floorId: dept.floorId, deptId: dept.id, agentId: form.agentId || routineAgentFor(state, dept),
    title: firstLineOf(prompt), prompt, schedule: dayFormSchedule(form, dayMs),
    autonomy: form.approve ? 'approve' : 'auto', outputFormat: 'text', paused: false,
  };
  const model = String(form.model || '').trim();
  if (model) body.model = model;
  return body;
}

/**
 * The live preview line of the popover: "Tâche programmée · samedi 26 septembre 2026 à 09:00" or
 * "Routine · le samedi à 09:00 · à partir du 26 sept. · premier passage sam. 26 sept. à 09:00".
 * @param {object} form
 * @param {number} dayMs
 * @param {{now?: number}} [opts]
 * @returns {{ok: boolean, text: string}}
 */
export function dayFormPreview(form, dayMs, { now = Date.now() } = {}) {
  if (!form.repeat) {
    if (!isTime(form.time)) return { ok: false, text: t('cal.form.timeRequired') };
    return { ok: true, text: t('cal.form.previewTask', { when: `${longDayLabel(dayMs)} à ${form.time}` }) };
  }
  const schedule = dayFormSchedule(form, dayMs);
  const problems = validateSchedule(schedule);
  if (problems.length) return { ok: false, text: problems[0] };
  const parts = [t('cal.form.previewRoutine', { rhythm: describeRhythm(schedule) })];
  const bounds = describeBounds(schedule, { now });
  if (bounds) parts.push(bounds);
  const first = nextRun(schedule, now);
  parts.push(first === null ? t('cal.form.noRun') : t('cal.form.previewFirst', { when: `${shortDayLabel(first)} à ${hhmmOf(first)}` }));
  return { ok: first !== null, text: parts.join(' · ') };
}

function hhmmOf(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Bounds of the "toutes les N heures" input. */
export const EVERY_HOURS_MAX = MAX_HOURLY_EVERY;

/** The date key a routine created on this day starts at (schedule.start). */
export function startKeyOf(dayMs) {
  return dayKey(dayMs);
}
